import { isBranchConstrained } from "../../../../auth/sessionAccess.ts";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAppSettings } from "../../../../hooks/useAppSettings.tsx";
import Layout from "../../../Layout.tsx";
import Title from "../../../Title.tsx";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/index.ts";
import { useFormik } from "formik";
import moment from "moment";
import { weeks } from "../../../../utils/constanst.js";
import CustomSelectField from "../../../formik/CustomSelectField.tsx";
import ConfigHorarioSemanalFilters from "./ConfigHorarioSemanalFilters.tsx";
import ConfigHorarioSemanalTable from "./ConfigHorarioSemanalTable.tsx";
import ConfigHorarioSemanalExportModal from "./ConfigHorarioSemanalExportModal.tsx";
import Loader from "../../../Loader.tsx";
import {
  readPositionsByBranchAndShift,
  readUniquePositionNamesByBranch,
  readSchedulesByBranchAndWeek,
  readHoursByBranch,
  deleteSchedules,
} from "../../../../services/branchSchedule.service.ts";
import { readPartnersByBranch } from "../../../../services/partner.service.ts";
import { readBranch } from "../../../../services/branch.service.ts";
import {
  saveBranchScheduleWeekend,
  findBranchScheduleWeekendByBranchAndWeek,
  updateBranchScheduleWeekend,
} from "../../../../services/branchScheduleWeekend.service.ts";
import { notification } from "../../../../utils/Notifications.tsx";
import { getDateByDayOfWeek } from "../../../../utils/constanst.js";
import { fetchFreeDaysByDateRangeAndPartnerIds, syncWeeklyRestDays } from "../../../../services/freeDay.service.ts";
import {
  BRANCH_SCHEDULES_SAVED_EVENT,
  notifyBranchSchedulesSaved,
} from "./scheduleKeyUtils.ts";
import { persistWeeklyBranchSchedule } from "./weeklyBranchScheduleSave.ts";

const ConfigHorarioSemanal = () => {
  useAppSettings();

  const { user } = useSelector((state: RootState) => state.auth);
  const { id: ipParams } = useParams();

  let id: number;
  if (isBranchConstrained(user)) {
    id = user.branchId;
  } else {
    id = Number(ipParams);
  }

  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [positions, setPositions] = useState<any[]>([]);
  const [schedules, setSchedules] = useState<any[]>([]);
  const [partners, setPartners] = useState<any[]>([]);
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState<number>(id);
  const [branchName, setBranchName] = useState<string>("");
  const [showExportModal, setShowExportModal] = useState(false);
  const tableRef = useRef<{
    getTableData: (schedulesSnapshot?: any[]) => any;
    fillWeekWithCurrentDay: () => boolean;
    syncWeekWithSchedule: (schedules: any[]) => boolean;
    exportCurrentTableAsPdf: (options: { branchName: string; weekName: string; notes?: string }) => Promise<void>;
    getWeeklySaveContext: () => {
      cellAssignments: Record<string, string>;
      initialCellAssignments: Record<string, string>;
      removedScheduleIds: number[];
    };
  }>(null);
  const [saving, setSaving] = useState(false);
  const [exportingTablePdf, setExportingTablePdf] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [freeDays, setFreeDays] = useState<any[]>([]);
  const [weekNotesMeta, setWeekNotesMeta] = useState<{ id?: number; notes?: string } | null>(null);
  const [branchHours, setBranchHours] = useState<any | null>(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [weekNotes, setWeekNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  const formik = useFormik({
    initialValues: {
      week: 1,
    },
    onSubmit: (values) => {},
  });

  const fetchDependencies = useCallback(
    async (branchId: number, weekId: number) => {
      if (weekId === undefined || weekId === null || !branchId) {
        return;
      }

      const weekData = weeks[weekId];
      if (!weekData || !weekData.value) {
        console.warn(`Semana con ID ${weekId} no encontrada o sin valor`);
        return;
      }

      const weekValue = weekData.value;

      setLoading(true);
      try {
        const positionsByShift = await Promise.all([
          readPositionsByBranchAndShift(branchId, "Matutino"),
          readPositionsByBranchAndShift(branchId, "Intermedio"),
          readPositionsByBranchAndShift(branchId, "Vespertino"),
        ]);

        const formattedPositions: any[] = [];

        ["Matutino", "Intermedio", "Vespertino"].forEach(
          (shift, shiftIndex) => {
            const shiftPositions = (positionsByShift[shiftIndex] || [])
              .slice()
              .sort((a: any, b: any) =>
                a.position.name.localeCompare(b.position.name, "es"),
              );

            shiftPositions.forEach((pos: any) => {
              const n = Math.max(0, Number(pos.numberOfWorkers) || 0);
              const isEssencial = Boolean(pos.isEssencial);
              if (n > 0) {
                for (let i = 0; i < n; i++) {
                  formattedPositions.push({
                    id: `${pos.position.id}-${shift}-${i}`,
                    positionId: pos.position.id,
                    positionName: pos.position.name,
                    positionDisplayName: `${pos.position.name} ${i + 1}`,
                    positionOrder: Number(pos.position.order) || 0,
                    shift: shift,
                    numberOfWorkers: n,
                    showSchedule: pos.showSchedule,
                    isEssencial,
                  });
                }
              } else {
                // Incluir puesto en plantilla aunque tenga 0 trabajadores (coincide con /branch-schedule/positions/branch/:id)
                formattedPositions.push({
                  id: `${pos.position.id}-${shift}-0`,
                  positionId: pos.position.id,
                  positionName: pos.position.name,
                  positionDisplayName: pos.position.name,
                  positionOrder: Number(pos.position.order) || 0,
                  shift: shift,
                  numberOfWorkers: 0,
                  showSchedule: pos.showSchedule,
                  isEssencial,
                });
              }
            });
          },
        );

        setPositions(
          formattedPositions.filter((position) => position.showSchedule),
        );

        const branchPartners = await readPartnersByBranch(branchId, {
          active: true,
        });
        const partnerIds = branchPartners.map((p: any) => p.id);

        const [
          scheduleData,
          uniquePositionNames,
          branchData,
          hoursData,
          freeDaysData,
          weekMetaData,
        ] = await Promise.all([
          readSchedulesByBranchAndWeek(branchId, weekValue),
          readUniquePositionNamesByBranch(branchId),
          readBranch(branchId),
          readHoursByBranch(branchId),
          partnerIds.length > 0
            ? fetchFreeDaysByDateRangeAndPartnerIds(weekValue, partnerIds).catch(
                () => {
                  notification(
                    "Error",
                    "No se pudieron cargar los días de descanso",
                    "danger",
                  );
                  return [];
                },
              )
            : Promise.resolve([]),
          findBranchScheduleWeekendByBranchAndWeek(branchId, weekValue),
        ]);

        setPartners(branchPartners);
        setWeekNotesMeta(
          weekMetaData
            ? { id: weekMetaData.id, notes: weekMetaData.notes ?? "" }
            : null,
        );
        setFreeDays(freeDaysData || []);
        setSchedules(Array.isArray(scheduleData) ? scheduleData : []);
        setAvailableRoles(uniquePositionNames);
        setBranchName(branchData?.name || "");
        setBranchHours(hoursData || null);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (id) {
      setSelectedBranchId(id);
    }
  }, [id]);

  useEffect(() => {
    if (
      selectedBranchId &&
      formik.values.week !== undefined &&
      formik.values.week !== null
    ) {
      fetchDependencies(selectedBranchId, formik.values.week);
    }
  }, [
    location.key,
    location.pathname,
    selectedBranchId,
    formik.values.week,
    fetchDependencies,
  ]);

  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{
        branchId?: number;
        weekIndex?: number;
      }>;
      const bid = ce.detail?.branchId;
      if (bid == null || Number(bid) !== Number(selectedBranchId)) return;
      const savedWeek = ce.detail?.weekIndex;
      // Comparar como número: el horario individual a veces manda string desde Formik.
      if (
        savedWeek !== undefined &&
        savedWeek !== null &&
        Number(savedWeek) !== Number(formik.values.week)
      ) {
        return;
      }
      fetchDependencies(selectedBranchId, formik.values.week);
    };
    window.addEventListener(BRANCH_SCHEDULES_SAVED_EVENT, handler);
    return () =>
      window.removeEventListener(BRANCH_SCHEDULES_SAVED_EVENT, handler);
  }, [selectedBranchId, formik.values.week, fetchDependencies]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const weekValue = weeks[formik.values.week]?.value;
      if (!weekValue) {
        notification("Error", "Semana no válida", "danger");
        return;
      }

      if (!tableRef.current) {
        notification(
          "Error",
          "No se pudo obtener los datos de la tabla",
          "danger",
        );
        return;
      }

      const weeklyCtx = tableRef.current.getWeeklySaveContext();
      const removedScheduleIds = new Set<number>(
        (weeklyCtx.removedScheduleIds || []).map((id: number) => Number(id)),
      );

      // Eliminar primero los registros marcados en UI (evita que el payload los reviva).
      if (removedScheduleIds.size > 0) {
        await Promise.all(
          Array.from(removedScheduleIds).map((scheduleId) =>
            deleteSchedules(scheduleId).catch((err) => {
              console.warn(
                `No se pudo eliminar horario id=${scheduleId}`,
                err,
              );
            }),
          ),
        );
      }

      const freshSchedules = await readSchedulesByBranchAndWeek(
        selectedBranchId,
        weekValue,
      );
      let schedulesForSave = Array.isArray(freshSchedules)
        ? freshSchedules
        : [];
      if (removedScheduleIds.size > 0) {
        schedulesForSave = schedulesForSave.filter(
          (s: any) => !removedScheduleIds.has(Number(s?.id)),
        );
      }

      const tableData = tableRef.current.getTableData(schedulesForSave);
      const { items, restDays } = tableData;

      await syncWeeklyRestDays(weekValue, restDays);

      await persistWeeklyBranchSchedule({
        branchId: selectedBranchId,
        weekValue,
        items,
        weeklyCtx,
        removedScheduleIds,
        branchHours,
        schedulesForSave,
      });

      await fetchDependencies(selectedBranchId, formik.values.week);
      setHasUnsavedChanges(false);

      notifyBranchSchedulesSaved(
        selectedBranchId,
        formik.values.week != null ? Number(formik.values.week) : undefined,
      );

      notification(
        "Éxito",
        "Horario semanal guardado correctamente",
        "success",
      );
    } catch (error: any) {
      console.error("Error saving schedule:", error);
      notification(
        "Error",
        error.response?.data?.message || "Error al guardar el horario semanal",
        "danger",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleFillWeek = () => {
    if (!tableRef.current) {
      notification(
        "Error",
        "No se pudo obtener los datos de la tabla",
        "danger",
      );
      return;
    }

    const result = tableRef.current.fillWeekWithCurrentDay();
    if (result) {
      notification(
        "Éxito",
        "Horario rellenado automáticamente con colaboradores y puestos que hacen match. No olvides guardar los cambios.",
        "success",
      );
    } else {
      notification(
        "Info",
        "No se encontraron colaboradores que hagan match con las posiciones",
        "info",
      );
    }
  };

  const handleSyncWithSchedule = async () => {
    if (!tableRef.current) {
      notification(
        "Error",
        "No se pudo obtener los datos de la tabla",
        "danger",
      );
      return;
    }

    setSaving(true);
    try {
      const weekValue = weeks[formik.values.week]?.value;
      if (!weekValue) {
        notification("Error", "Semana no válida", "danger");
        return;
      }

      const schedules = await readSchedulesByBranchAndWeek(
        selectedBranchId,
        weekValue,
      );

      if (!schedules || schedules.length === 0) {
        notification(
          "Info",
          "No se encontraron horarios configurados para esta semana",
          "info",
        );
        return;
      }

      const result = tableRef.current.syncWeekWithSchedule(schedules);
      if (!result) {
        notification(
          "Info",
          "No se pudieron sincronizar horarios para las posiciones actuales",
          "info",
        );
        return;
      }

      const weeklyCtx = tableRef.current.getWeeklySaveContext();
      const removedScheduleIds = new Set<number>(
        (weeklyCtx.removedScheduleIds || []).map((id: number) => Number(id)),
      );

      if (removedScheduleIds.size > 0) {
        await Promise.all(
          Array.from(removedScheduleIds).map((scheduleId) =>
            deleteSchedules(scheduleId).catch((err) => {
              console.warn(
                `No se pudo eliminar horario id=${scheduleId}`,
                err,
              );
            }),
          ),
        );
      }

      const freshSchedules = await readSchedulesByBranchAndWeek(
        selectedBranchId,
        weekValue,
      );
      let schedulesForSave = Array.isArray(freshSchedules)
        ? freshSchedules
        : [];
      if (removedScheduleIds.size > 0) {
        schedulesForSave = schedulesForSave.filter(
          (s: any) => !removedScheduleIds.has(Number(s?.id)),
        );
      }

      const tableData = tableRef.current.getTableData(schedulesForSave);
      const { items, restDays } = tableData;

      await syncWeeklyRestDays(weekValue, restDays);
      await persistWeeklyBranchSchedule({
        branchId: selectedBranchId,
        weekValue,
        items,
        weeklyCtx,
        removedScheduleIds,
        branchHours,
        schedulesForSave,
      });

      await fetchDependencies(selectedBranchId, formik.values.week);
      setHasUnsavedChanges(false);

      notifyBranchSchedulesSaved(
        selectedBranchId,
        formik.values.week != null ? Number(formik.values.week) : undefined,
      );

      notification(
        "Éxito",
        "Semana sincronizada con los horarios diarios",
        "success",
      );
    } catch (error: any) {
      console.error("Error syncing schedule:", error);
      notification(
        "Error",
        error.response?.data?.message ||
          "Error al sincronizar con los horarios",
        "danger",
      );
    } finally {
      setSaving(false);
    }
  };

  const weekDays = [
    "Lunes",
    "Martes",
    "Miercoles",
    "Jueves",
    "Viernes",
    "Sabado",
    "Domingo",
  ];

  const openNotesModal = () => {
    setWeekNotes(weekNotesMeta?.notes ?? "");
    setShowNotesModal(true);
  };

  const handleSaveNotes = async () => {
    const weekData = weeks[formik.values.week];
    const weekValue = weekData?.value;
    if (!weekValue || !selectedBranchId) return;
    setSavingNotes(true);
    try {
      if (weekNotesMeta?.id) {
        await updateBranchScheduleWeekend(weekNotesMeta.id, { notes: weekNotes });
        setWeekNotesMeta((prev) =>
          prev ? { ...prev, notes: weekNotes } : { notes: weekNotes },
        );
      } else {
        const start = weekData?.exactDates?.start;
        const end = weekData?.exactDates?.end;
        if (!start || !end) {
          notification("Aviso", "No se pudo obtener el rango de la semana", "warning");
          return;
        }
        const weekStart = moment(start).format("YYYY-MM-DD");
        const weekEnd = moment(end).format("YYYY-MM-DD");
        const created = await saveBranchScheduleWeekend({
          branchId: selectedBranchId,
          weekValue,
          weekStart,
          weekEnd,
          items: [],
          restDays: [],
          notes: weekNotes || undefined,
        });
        setWeekNotesMeta({ id: created?.id, notes: weekNotes });
      }
      notification("Listo", "Notas guardadas correctamente", "success");
      setShowNotesModal(false);
    } catch (e: any) {
      notification("Error", e?.message || "Error al guardar las notas", "danger");
    } finally {
      setSavingNotes(false);
    }
  };

  const handleDownloadTablePdf = async () => {
    if (!tableRef.current) {
      notification("Error", "No se pudo obtener los datos de la tabla", "danger");
      return;
    }
    setExportingTablePdf(true);
    try {
      await tableRef.current.exportCurrentTableAsPdf({
        branchName: branchName || "Sucursal",
        weekName: weeks[formik.values.week]?.name || "Semana",
        notes: (weekNotesMeta?.notes ?? weekNotes) || undefined,
      });
      notification("Éxito", "Tabla descargada como PDF correctamente", "success");
    } catch (error: any) {
      console.error("Error exporting table PDF:", error);
      notification("Error", error?.message || "Error al descargar la tabla en PDF", "danger");
    } finally {
      setExportingTablePdf(false);
    }
  };

  const handleDayClick = (
    day: string,
    positionName?: string,
    positionShift?: string,
  ) => {
    const dayIndex = weekDays.indexOf(day);
    navigate(`/sucursales/horario/${selectedBranchId}`, {
      state: {
        dayOfWeek: dayIndex,
        week: formik.values.week,
        shift: 3,
        positionName,
        positionShift,
      },
    });
  };

  return (
    <Layout>
      <Title
        baseTitle="Configuración"
        basePath=""
        title="Configurar Horario Semanal"
      />

      <ConfigHorarioSemanalFilters
        formik={formik}
        branchId={selectedBranchId}
        initialBranchId={id}
        onBranchChange={(newBranchId) => {
          setSelectedBranchId(newBranchId);
        }}
        canChangeBranch={!isBranchConstrained(user)}
        onFilterChange={() => {}}
        branchName={branchName}
        onExportClick={() => setShowExportModal(true)}
        onSaveClick={handleSave}
        onFillWeekClick={handleFillWeek}
        onSyncWithScheduleClick={handleSyncWithSchedule}
        saving={saving}
        hasUnsavedChanges={hasUnsavedChanges}
        onRefreshClick={() =>
          fetchDependencies(selectedBranchId, formik.values.week)
        }
        onNotesClick={openNotesModal}
        onDownloadTablePdf={handleDownloadTablePdf}
        exportingTablePdf={exportingTablePdf}
      />

      {loading ? (
        <Loader isLoading={loading} />
      ) : (
        <ConfigHorarioSemanalTable
          ref={tableRef}
          positions={positions}
          schedules={schedules}
          partners={partners}
          weekDays={weekDays}
          weekValue={weeks[formik.values.week]?.value}
          branchId={selectedBranchId}
          availableRoles={availableRoles}
          freeDays={freeDays}
          branchHours={branchHours}
          scheduleViewMode={true}
          onDayClick={handleDayClick}
          onUnsavedChange={setHasUnsavedChanges}
        />
      )}

      <ConfigHorarioSemanalExportModal
        open={showExportModal}
        onClose={() => setShowExportModal(false)}
        positions={positions}
        schedules={schedules}
        partners={partners}
        weekDays={weekDays}
        weekValue={weeks[formik.values.week]?.value || ""}
        branchName={branchName}
        weekName={weeks[formik.values.week]?.name || ""}
      />

      {showNotesModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1050,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0,0,0,0.4)",
          }}
          onClick={() => !savingNotes && setShowNotesModal(false)}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "12px",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
              padding: "24px",
              minWidth: "400px",
              maxWidth: "90vw",
              maxHeight: "80vh",
              overflow: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: "0 0 16px", fontSize: "18px", fontWeight: 600, color: "#111827" }}>
              Notas de la semana
            </h3>
            <p style={{ margin: "0 0 12px", fontSize: "13px", color: "#6B7280" }}>
              {weeks[formik.values.week]?.name || "Semana actual"}
            </p>
            <textarea
              value={weekNotes}
              onChange={(e) => setWeekNotes(e.target.value)}
              placeholder="Escribe notas para esta semana..."
              rows={5}
              style={{
                width: "100%",
                padding: "12px",
                fontSize: "14px",
                border: "1px solid #D1D5DB",
                borderRadius: "8px",
                resize: "vertical",
                boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "16px" }}>
              <button
                type="button"
                onClick={() => !savingNotes && setShowNotesModal(false)}
                style={{
                  padding: "10px 18px",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#6B21A8",
                  backgroundColor: "#F3E8FF",
                  border: "1px solid #E9D5FF",
                  borderRadius: "8px",
                  cursor: savingNotes ? "not-allowed" : "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  if (!savingNotes) {
                    e.currentTarget.style.backgroundColor = "#EDE9FE";
                    e.currentTarget.style.borderColor = "#C4B5FD";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#F3E8FF";
                  e.currentTarget.style.borderColor = "#E9D5FF";
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSaveNotes}
                disabled={savingNotes}
                style={{
                  padding: "10px 18px",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#1D4ED8",
                  backgroundColor: savingNotes ? "#E0E7FF" : "#DBEAFE",
                  border: "1px solid #93C5FD",
                  borderRadius: "8px",
                  cursor: savingNotes ? "not-allowed" : "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  if (!savingNotes) {
                    e.currentTarget.style.backgroundColor = "#BFDBFE";
                    e.currentTarget.style.borderColor = "#60A5FA";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = savingNotes ? "#E0E7FF" : "#DBEAFE";
                  e.currentTarget.style.borderColor = "#93C5FD";
                }}
              >
                {savingNotes ? "Guardando..." : "Guardar notas"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ConfigHorarioSemanal;
